import i18next from 'i18next';

/**
 * Used when i18next reports no usable language, e.g. before `init` has run or in
 * unit tests that never initialize it. German is the primary product language.
 */
const FALLBACK_LOCALE = 'de-DE';

/**
 * Maximization is a pure function of the tag, so a cached entry can never go
 * stale. Worth caching because `currentIntlLocale` backs the default `locale`
 * parameter of every formatting helper in this package, so it runs once per
 * formatted grid cell and chart tick.
 */
const intlLocaleByLanguage = new Map<string, string>();

function maximizeToIntlLocale(language: string): string {
  try {
    const maximized = new Intl.Locale(language).maximize();

    return maximized.region
      ? `${maximized.language}-${maximized.region}`
      : FALLBACK_LOCALE;
  } catch {
    return FALLBACK_LOCALE;
  }
}

/**
 * The active UI language as a region-qualified BCP 47 tag, for `Intl` and other
 * locale-aware APIs.
 *
 * i18next reports a bare language such as `de`, which `Intl` resolves on its own,
 * but date libraries and locale-keyed lookups need an explicit region. The region
 * comes from CLDR likely-subtags, so any language resolves without a hardcoded
 * list. Tags with no likely region — i18next's `cimode` among them — fall back.
 *
 * Callers that need the language identity itself (keying a map of date-fns
 * locales, for example) want the app's own `currentLocale`, not this.
 */
export function currentIntlLocale(): string {
  const language = i18next.resolvedLanguage ?? i18next.language;

  if (!language) {
    return FALLBACK_LOCALE;
  }

  const cached = intlLocaleByLanguage.get(language);

  if (cached !== undefined) {
    return cached;
  }

  const intlLocale = maximizeToIntlLocale(language);

  intlLocaleByLanguage.set(language, intlLocale);

  return intlLocale;
}
